// @flow
import React, {Component} from 'react'
import {observer} from 'mobx-react'
import get from 'lodash/get'
import isEmpty from 'lodash/isEmpty'
import * as XLSX from 'xlsx'

import Switch from './Switch'
import api from '../../../src/api/client'
import store from '../store'
import MedicineTable from './MedicineTable'
import MedicineInfoAdder from './MedicineInfoAdder'
import Collapsible from './Collapsible'
import {getMedicinesFromExcelData} from '../helpers'

import css from './MedicineManagement.css'

const PAGE_SIZE = 40

const SheetJSFT = [
  'application/vnd.ms-excel', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
].map(function(x) {return x}).join(',')

const colHeaders = [
  'activeIncr',
  'name',
  'intensity',
  'pharmForm',
  'atc',
  'vnr',
  'salesPermit',
  'storCond',
  'dimensions',
  'colour',
  'breakline',
  'markings',
  'leaflet',
  'note',
  'imageUrl',
]

class DragDropFile extends Component {
  suppress(evt) {
    evt.stopPropagation()
    evt.preventDefault()
  }

  onDrop = (evt) => {
    evt.stopPropagation()
    evt.preventDefault()
    const files = evt.dataTransfer.files

	  if(files && files[0]) {
      this.props.handleFile(files[0])
    }
  }

  render() {
    return (
      <div
        onDrop={this.onDrop}
        onDragEnter={this.suppress}
        onDragOver={this.suppress}>
	      {this.props.children}
      </div>
    )
  }
}

type DataInputProps = {
  fileName: string,
  handleFile: Function,
}
class DataInput extends Component {
  props: DataInputProps

  handleChange = (e) => {
	  const files = e.target.files
    if(files && files[0]) {
      this.props.handleFile(files[0])
    }
  }
  render() {
    const {fileName} = this.props
    return (
      <div className={css.fileInputWrapper}>
        <div>
          <label htmlFor="file" className={css.fileLabel}>Valitse tiedosto</label>
          <input
            type="file"
            accept={SheetJSFT}
            className={css.fileInput}
            id="file"
            onChange={this.handleChange} />
        </div>
        <div>
          <p className={css.fileName}>{fileName}</p>
        </div>
      </div>
	   )
  }
}

type State = {
  hasSettingsUpdated: boolean,
  hasSettingsUpdateFailed: boolean,
  hasMedicinesUpdated: boolean,
  hasMedicineUpdateFailed: boolean,
  hasLabelsUpdated: boolean,
  hasLabelsUpdateFailed: boolean,
  fileName: string,
}
type Props = {
  show: boolean,
  onUnauthorized: Function,
}
@observer
class MedicineManagement extends Component {
  props: Props

  state: State = {
    hasSettingsUpdated: false,
    hasSettingsUpdateFailed: false,
    hasMedicinesUpdated: false,
    hasMedicineUpdateFailed: false,
    hasLabelsUpdated: false,
    hasLabelsUpdateFailed: false,
    fileName: '',
  }
  componentWillMount() {
    this.getSettings()
    this.getMedicineList()
    this.getFooterLabels()
  }

  getSettings = async() => {
    try {
      const settings = await api.resources.settings.getSettings()
      if(!isEmpty(settings)) {
        const isMedicineInfoVisible = settings.showMedicineInfo
        store.saveIsMedicineInfoVisible(isMedicineInfoVisible ? true : false)
      }
    } catch(e) {
      console.log(e)
    }
  }

  getMedicineList = async() => {
    try {
      const medicines = await api.resources.medicine.getMedicines()
      store.saveMedicines(medicines)
      store.saveActiveMedicinePage(0)
      const pagedMedicines = []
      for(let i = 0; i < PAGE_SIZE; i++) {
        if(i >= medicines.length) {
          break
        }
        pagedMedicines.push(medicines[i])
      }
      store.savePagedMedicines(pagedMedicines)
      store.saveMaxMedicinePage(Math.ceil(medicines.length / PAGE_SIZE))
    } catch(e) {
      console.log(e)
    }
  }

  getFooterLabels = async () => {
    // Fetch footer label data only if empty
    const footerLabels = await api.resources.footer.getFooterLabels()
    const footerLabelObj = {
      searchTitle: get(footerLabels, 'search_title', ''),
      searchDescription: get(footerLabels, 'search_description', ''),
      searchPlaceholder: get(footerLabels, 'search_placeholder', ''),
      searchInstruction: get(footerLabels, 'search_instruction', ''),
      company: get(footerLabels, 'footer_company', ''),
      description: get(footerLabels, 'footer_description', ''),
      phone: get(footerLabels, 'footer_phone', ''),
      copyright: get(footerLabels, 'footer_copyright', ''),
      queueInfo: get(footerLabels, 'footer_queueInfo', ''),
    }
    store.saveFooterLabels(footerLabelObj)
    store.saveSavedFooterLabels(footerLabelObj)
  }

  toggleIsMedicineInfoVisible = () => {
    const isVisible = store.isMedicineInfoVisible
    store.saveIsMedicineInfoVisible(!isVisible)
    this.setState({
      hasSettingsUpdated: false,
    })
  };

  handleFile = (file: any) => {
    const reader:any = new FileReader()
    reader.onload = (e) => {
      const bstr = e.target.result
      const wb = XLSX.read(bstr, {type:'binary'})

      /* Get first worksheet */
      const wsname = wb.SheetNames[0]
      const ws = wb.Sheets[wsname]

      /* Convert array of arrays */
      const data = XLSX.utils.sheet_to_json(ws, {header: colHeaders})

      const medicines = getMedicinesFromExcelData(data)
      store.saveNewMedicines(medicines)
    }
    reader.readAsBinaryString(file)

    this.setState({
      hasMedicinesUpdated: false,
      fileName: file.name,
    })
  }

  onUnauthorizedError = () => {
    const {onUnauthorized} = this.props
    this.setState({
      hasMedicineUpdateFailed: false,
      hasMedicinesUpdated: false,
      hasSettingsUpdateFailed: false,
      hasSettingsUpdated: false,
      hasLabelsUpdateFailed: false,
      hasLabelsUpdated: false,
    })
    onUnauthorized()
  }

  updateMedicines = async() => {
    try {
      const medicines = store.newMedicines
      const response = await api.resources.medicine.updateMedicines(medicines)
      if(response) {
        this.setState({
          hasMedicinesUpdated: true,
          hasMedicineUpdateFailed: false,
          fileName: '',
        })
        store.saveMedicines(medicines)
        store.saveActiveMedicinePage(0)
        const pagedMedicines = []
        for(let i = 0; i < PAGE_SIZE; i++) {
          if(i >= medicines.length) {
            break
          }
          pagedMedicines.push(medicines[i])
        }
        store.savePagedMedicines(pagedMedicines)
        store.saveMaxMedicinePage(Math.ceil(medicines.length / PAGE_SIZE))
        store.saveNewMedicines([])
      }
    } catch(e) {
      switch (e.status) {
        case '401':
          this.onUnauthorizedError()
          break
        default:
          this.setState({
            hasMedicineUpdateFailed: true,
            hasMedicinesUpdated: false,
          })
          store.saveNewMedicines([])
      }
    }
  }

  saveSettings = async() => {
    try {
      const value = store.isMedicineInfoVisible
      const response = await api.resources.settings.updateSettings({showMedicineInfo: value})
      if(response) {
        this.setState({
          hasSettingsUpdated: true,
        })
      }
    } catch (e) {
      switch (e.status) {
        case '401':
          this.onUnauthorizedError()
          break
        default:
          this.setState({
            hasSettingsUpdateFailed: true,
            hasSettingsUpdated: false,
          })
      }
    }
  }

  updateFooterLabels = async() => {
    try {
      const footerLabels = store.footerLabels
      const footerLabelsArray = [
        {uiId: 'search_title', text: get(footerLabels, 'searchTitle', '')},
        {uiId: 'search_description', text: get(footerLabels, 'searchDescription', '')},
        {uiId: 'search_placeholder', text: get(footerLabels, 'searchPlaceholder', '')},
        {uiId: 'search_instruction', text: get(footerLabels, 'searchInstruction', '')},
        {uiId: 'footer_company', text: get(footerLabels, 'company', '')},
        {uiId: 'footer_description', text: get(footerLabels, 'description', '')},
        {uiId: 'footer_phone', text: get(footerLabels, 'phone', '')},
        {uiId: 'footer_copyright', text: get(footerLabels, 'copyright', '')},
        {uiId: 'footer_queueInfo', text: get(footerLabels, 'queueInfo', '')},
      ]
      const response = await api.resources.footer.updateFooterLabels(footerLabelsArray)
      if(response) {
        store.saveSavedFooterLabels(footerLabels)
        this.setState({
          hasLabelsUpdated: true,
        })
      }
    } catch(e) {
      switch (e.status) {
        case '401':
          this.onUnauthorizedError()
          break
        default:
          this.setState({
            hasLabelsUpdateFailed: true,
            hasLabelsUpdated: false,
          })
      }
    }
  }

  handleTextInputChange = (e: any, field: string) => {
    const footerLabels = store.footerLabels
    footerLabels[field] = e.target.value
    store.saveFooterLabels(footerLabels)
    this.setState({hasLabelsUpdated: false})
  }

  areFooterLabelsValid = () => {
    // const footerLabels = store.footerLabels

    // return footerLabels.company && footerLabels.description && footerLabels.phone && footerLabels.copyright && footerLabels.queueInfo
    return true
  }

  setMedicineTableActivePage = (page: number) => {
    const {medicines} = store
    const pagedMedicines = []
    for(let i = 0; i < PAGE_SIZE; i++) {
      if(((page * PAGE_SIZE) + i) >= medicines.length ) {
        break
      }
      pagedMedicines.push(medicines[(page * PAGE_SIZE) + i])
    }
    store.savePagedMedicines(pagedMedicines)
    store.saveActiveMedicinePage(page)
  }

  render () {
    const {medicines, pagedMedicines, maxMedicinePages, activeMedicinePage, newMedicines, isMedicineInfoVisible, footerLabels} = store
    const {company, description, phone, copyright, searchTitle, searchDescription, searchPlaceholder, searchInstruction, queueInfo} = footerLabels
    const {hasSettingsUpdated, hasSettingsUpdateFailed, hasMedicinesUpdated, hasMedicineUpdateFailed,  fileName, hasLabelsUpdated, hasLabelsUpdateFailed} = this.state
    const {show} = this.props
    const areLabelsValid = this.areFooterLabelsValid()

    if(!show) {
      return null
    }
    return(
      <div className={css.component}>
        <div className={css.wrapper}>
          <h1 className={css.title}>Annosjakelun sisällönhallinta</h1>

          <Collapsible title={'Asetukset'}>
            <p>Näytä lääkeopas</p>
            <Switch
              value={isMedicineInfoVisible}
              onToggle={() => {
                this.toggleIsMedicineInfoVisible()
              }}
            />
            <button className={css.button} onClick={() => this.saveSettings()}>Tallenna asetukset</button>
            {hasSettingsUpdated && (
              <p className={css.hasUpdated}>Asetukset on tallennettu</p>
            )}
            {hasSettingsUpdateFailed && (
              <p className={css.hasFailed}>Asetusten tallennus epäonnistui</p>
            )}
          </Collapsible>

          <Collapsible title={'Päivitä tuotetiedot'}>
            <DragDropFile handleFile={this.handleFile}>
  	           <DataInput handleFile={this.handleFile} fileName={fileName} />
  	        </DragDropFile>
            {hasMedicinesUpdated && (
              <p className={css.hasUpdated}>Tuotetiedot on päivitetty</p>
            )}
            {hasMedicineUpdateFailed && (
              <p className={css.hasFailed}>Tuotetietojen päivitys epäonnistui</p>
            )}
            {newMedicines.length > 0 && (
              <button className={css.button} onClick={() => this.updateMedicines()}>Päivitä tuotetiedot</button>
            )}
            {medicines.length > 0 && (
              <div>
                <h2>Tallennetut tuotetiedot</h2>
                <MedicineTable medicines={pagedMedicines} maxPage={maxMedicinePages} activePage={activeMedicinePage} onActivePageChange={(page) => this.setMedicineTableActivePage(page)}/>
              </div>
            )}
            {medicines.length === 0 && (
              <p>Ei tallennettuja tuotetietoja</p>
            )}
          </Collapsible>

          <Collapsible title={'Päivitä lääkeoppaan data'}>
            <MedicineInfoAdder />
          </Collapsible>

          <Collapsible title={'Päivitä käyttöliittymän tekstejä'}>
            <h2>Hakusivun tekstit</h2>
            <p>Hakusivun otsikko</p>
            <input type={'text'} className={css.textInput} placeholder={'Hakusivun otsikko'} onChange={(e) => this.handleTextInputChange(e, 'searchTitle')} value={searchTitle}/>

            <p>Hakusivun kuvausteksti</p>
            <input type={'text'} className={css.textInput} placeholder={'Hakusivun kuvaus'} onChange={(e) => this.handleTextInputChange(e, 'searchDescription')} value={searchDescription}/>

            <p>Hakukentän placeholder</p>
            <input type={'text'} className={css.textInput} placeholder={'Hakukentän placeholder'} onChange={(e) => this.handleTextInputChange(e, 'searchPlaceholder')} value={searchPlaceholder}/>

            <p>Hakukentän ohjeteksti</p>
            <input type={'text'} className={css.textInput} placeholder={'Hakukentän ohjeteksti'} onChange={(e) => this.handleTextInputChange(e, 'searchInstruction')} value={searchInstruction}/>

            <h2>Alatunnisteen tekstit</h2>
            <p>Yrityksen nimi</p>
            <input type={'text'} className={css.textInput} placeholder={'Yrityksen nimi'} onChange={(e) => this.handleTextInputChange(e, 'company')} value={company}/>

            <p>Kuvaus</p>
            <input type={'text'} className={css.textInput} placeholder={'Kuvaus'} onChange={(e) => this.handleTextInputChange(e, 'description')} value={description}/>

            <p>Puhelinnumero</p>
            <input type={'text'} className={css.textInput} placeholder={'Puhelinnumero'} onChange={(e) => this.handleTextInputChange(e, 'phone')} value={phone}/>

            <p>Jonotuksen infoteksti</p>
            <input type={'text'} className={css.textInput} placeholder={'Jonotuksen infoteksti'} onChange={(e) => this.handleTextInputChange(e, 'queueInfo')} value={queueInfo}/>

            <p>Copyright-teksti</p>
            <input type={'text'} className={css.textInput} placeholder={'Copyright-teksti'} onChange={(e) => this.handleTextInputChange(e, 'copyright')} value={copyright}/>

            <button disabled={!areLabelsValid} className={css.button} onClick={() => this.updateFooterLabels()}>Päivitä käyttöliittymän tekstit</button>
            {hasLabelsUpdated && (
              <p className={css.hasUpdated}>Käyttöliittymän tekstit on päivitetty</p>
            )}
            {hasLabelsUpdateFailed && (
              <p className={css.hasFailed}>Käyttöliittymän päivitys epäonnistui</p>
            )}
          </Collapsible>
        </div>
      </div>
    )
  }
}

export default MedicineManagement



// WEBPACK FOOTER //
// ./src/admin/components/MedicineManagement.js